/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo } from 'react';

import { jsx } from '@compiled/react';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { MenuStateContext } from '../../../context';
import type {
	MenuStateContextProps,
	UniversalCreateButtonCombinedProps,
	UniversalCreateButtonProps,
	UniversalCreateButtonPropsV2,
} from '../../../types';
import AnalyticsWrapper from '../../components/AnalyticsWrapper';
import { LoadingMenu } from '../../components/LoadingMenu';
import { PopupTrigger } from '../../components/PopupTrigger';
import { TriggerButton } from '../../components/TriggerButton';
import { useIsResponsiveView } from '../../utils';

import { popupContentEntryPoint } from './entrypoint';

/**
 * Type guard to check if props are v1 (has 'items' property)
 */
function isV1Props(props: UniversalCreateButtonCombinedProps): props is UniversalCreateButtonProps {
	return 'items' in props;
}

/**
 * Type guard to check if props are v2 (has 'sections' property)
 */
function isV2Props(
	props: UniversalCreateButtonCombinedProps,
): props is UniversalCreateButtonPropsV2 {
	return 'sections' in props;
}

function UniversalCreateButtonInner(props: UniversalCreateButtonCombinedProps) {
	const {
		children,
		testId,
		onClick,
		onClose,
		enableTriggerPulse = false,
		triggerButtonSpotlightTarget,
		isNav4Enabled = true,
		buttonTheme,
		onMouseEnter,
		onMouseLeave,
		shouldRenderToParent,
		primaryActionItem,
	} = props;

	const isResponsive = useIsResponsiveView();

	const handleTriggerButtonClick = useCallback(() => {
		onClick?.();
	}, [onClick]);

	const handleClose = useCallback(() => {
		onClose?.();
	}, [onClose]);

	const handleMouseEnter = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseEnter?.(event);
		},
		[onMouseEnter],
	);

	const menuStateContextValue = useMemo((): MenuStateContextProps => {
		const isV2Mode = isV2Props(props) && fg('navx_uc_cross_app_creation_v2_enabled');

		return {
			isV2Mode,
			items: isV1Props(props) ? props.items : [],
			product: props.product ?? 'unknown',
			subProduct: props.subProduct,
			isNav4Enabled,
			sections: isV2Mode ? props.sections : [],
			crossAppCreationConfig: isV2Mode
				? (props.crossAppCreationConfig ?? ({ enabled: false } as const))
				: ({ enabled: false } as const),
			suggestionConfig:
				'suggestionConfig' in props
					? (props.suggestionConfig ?? { enabled: false })
					: { enabled: false },
			selectedItem: props.selectedItem,
			preventClose: props.preventClose,
			controlledOpen: props.isOpen,
		};
	}, [props, isNav4Enabled]);

	return (
		<MenuStateContext.Provider value={menuStateContextValue}>
			<PopupTrigger
				placement={
					(isResponsive && isNav4Enabled) || primaryActionItem ? 'bottom-end' : 'bottom-start'
				}
				// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
				shouldRenderToParent={shouldRenderToParent}
				onClose={handleClose}
				trigger={(triggerProps) => (
					<TriggerButton
						triggerProps={triggerProps}
						testId={testId}
						triggerButtonSpotlightTarget={triggerButtonSpotlightTarget}
						enableTriggerPulse={enableTriggerPulse}
						isOpen={triggerProps['aria-expanded']}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={onMouseLeave}
						onClick={handleTriggerButtonClick}
						isNav4Enabled={isNav4Enabled}
						buttonTheme={buttonTheme}
						primaryActionItem={primaryActionItem}
					>
						{children}
					</TriggerButton>
				)}
				entryPoint={popupContentEntryPoint}
				entryPointProps={props}
				fallback={<LoadingMenu padded />}
			/>
		</MenuStateContext.Provider>
	);
}

export function UniversalCreateButtonLazy(props: UniversalCreateButtonCombinedProps) {
	return (
		<ConditionalRelayEnvironmentProvider>
			<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
				<AnalyticsWrapper>
					<FlagsProvider>
						<UniversalCreateButtonInner {...props} />
					</FlagsProvider>
				</AnalyticsWrapper>
			</BFFProvider>
		</ConditionalRelayEnvironmentProvider>
	);
}
